import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnChanges, inject, output, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { OfferSource } from '@cds';
import { DisplayType, Parameter } from '@cds/betting-offer';
import { OptionGroupType } from '@frontend/sports/betting-offer/feature/fixture-factories';
import {
    BetBuilderBadgeType,
    EventModel,
    EventOptionGroup,
    EventOptionGroupType,
    Tv2MarketParameters,
} from '@frontend/sports/betting-offer/feature/model';
import { MarketSubType, MarketType, PeriodType } from '@frontend/sports/betting-offer/feature/types';
import {
    BetBuilderConfig,
    EpcotConfig,
    EventDetailsConfig,
    LayoutNavigationConfig,
    MarketGroupingConfig,
    Sitecore,
} from '@frontend/sports/common/client-config-data-access';
import { injectCurrentTheme } from '@frontend/sports/common/ui/shared';
import { DeviceService } from '@frontend/vanilla/core';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { NgxFloatUiPlacements } from 'ngx-float-ui';

import { SGPBadgeComponent } from '../bet-builder-core/components/badge/sgp-badge/sgp-badge.component';
import { isProviderSupportingBadges } from '../bet-builder-core/utils';
import { MarketSubtypeLogoComponent } from '../marketsubtype-logo/marketsubtype-logo.component';
import { MarketSubTypeLogoService } from '../marketsubtype-logo/marketsubtype-logo.service';
import { EventTimerComponent } from '../timers/event-timer.component';
import { EachWayMarketHelperService } from './each-way-market-helper.service';
import { ExtendedOfferComponent } from './extended-offer.component';
import { MarketHeaderBetBuilderBadgeComponent } from './market-header-bet-builder-badge/market-header-bet-builder-badge.component';
import { MarketHelpToggleComponent } from './market-help/market-help-toggle.component';
import { ToggleViewButtonComponent } from './toggle-view-button.component';

@Component({
    selector: 'ms-option-panel-header',
    templateUrl: './option-panel-header.component.html',
    styleUrl: './option-panel-header.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        NgClass,
        EventTimerComponent,
        IconCustomComponent,
        MarketHelpToggleComponent,
        ExtendedOfferComponent,
        MarketSubtypeLogoComponent,
        ToggleViewButtonComponent,
        MarketHeaderBetBuilderBadgeComponent,
        SGPBadgeComponent,
    ],
})
export class OptionPanelHeaderComponent implements OnChanges {
    @Input() groupType: OptionGroupType;
    @Input() optionGroup: EventOptionGroup;
    @Input() showHelp: boolean;
    @Input() toggleable: boolean;
    @Input() collapsed: boolean;
    @Input() activePeriodTab?: string;
    @Input() promoted: boolean;
    @Input() showMarketHelp: boolean;
    @Input() eventHelpText?: string;
    @Input() event: EventModel;
    @Input() marketLogoShow: boolean;
    @Input() showEventName = false;
    @Input() showEventTimer = false;
    @Input() showEventChevron = false;
    @Input() isLinkToEvent = true;
    @Input() appendTooltipToWidgetSlot = false;
    @Input() showInlineHelpText = false;
    readonly collapsedChange = output<boolean>();
    readonly showMarketHelpChange = output<boolean>();
    readonly eventTitleClicked = output<void>();

    enableCorrectScoreOptionGroupToggle = false;
    winMarketName?: string;
    shouldShowChevron: boolean;
    isEpcotEnabled: boolean;
    showSuspendedSelection: boolean;
    marketType?: MarketType;
    marketSubType?: MarketSubType;
    templateId?: string;
    marketLogoShowInHeader?: boolean;
    tooltipAppendto?: string;
    isPrecreatedBuildABet: boolean;

    private readonly currentTheme = injectCurrentTheme();
    private readonly isBetMgmTheme: boolean = ['mgm-dice'].includes(this.currentTheme);
    readonly sitecore = inject(Sitecore);
    readonly betBuilderConfig = inject(BetBuilderConfig);
    readonly eventDetailsConfig = inject(EventDetailsConfig);
    private readonly epcotConfig = inject(EpcotConfig);
    private readonly eachWayMarketHelperService = inject(EachWayMarketHelperService);
    private readonly marketSubTypeLogoService = inject(MarketSubTypeLogoService);
    readonly deviceService = inject(DeviceService);
    private readonly marketGroupingConfig = inject(MarketGroupingConfig);
    private readonly layoutConfig = inject(LayoutNavigationConfig);
    private readonly route = inject(ActivatedRoute);

    protected badgeTypeToShow = signal<BetBuilderBadgeType | undefined>(undefined);

    constructor() {
        this.isEpcotEnabled = this.epcotConfig.isEnabled;
        this.showSuspendedSelection = this.eventDetailsConfig.isSuspendedSelectionVisibilityEnabled;
    }

    ngOnChanges(): void {
        this.isPrecreatedBuildABet = this.groupType === OptionGroupType.PrecreatedBuildABet;
        const tooltipAppendTo = this.appendTooltipToWidgetSlot ? 'ms-widget-slot' : 'ms-event-details-main';
        this.tooltipAppendto =
            this.layoutConfig.isDynamicCategoryNavigationEnabled && !!this.route.snapshot.queryParams.dynamicCategory
                ? 'ms-fixture-list'
                : tooltipAppendTo;
        this.setMarketParameters();
        this.winMarketName = this.eachWayMarketHelperService.getWinMarketName(this.optionGroup, this.event);
        this.shouldShowChevron = this.collapsed || this.eventDetailsConfig.collapsibleChevron;
        this.setOptionGroupName();
        this.badgeTypeToShow.set(this.getBadgeTypeToShow());
    }

    private isBetBuilderBadgeVisible(): boolean {
        return (
            this.eventDetailsConfig.isBetBuilderSignpostingBadgeEnabled &&
            this.optionGroup.hasBetBuilderMarkets &&
            isProviderSupportingBadges(this.event.betBuilderInfo)
        );
    }

    private getBadgeTypeToShow(): BetBuilderBadgeType | undefined {
        if (this.isBetMgmTheme) {
            const isSgpBadgeVisible = this.eventDetailsConfig.isBetBuilderSignpostingBadgeEnabled && this.isPrecreatedBuildABet;

            return isSgpBadgeVisible || this.isBetBuilderBadgeVisible() ? BetBuilderBadgeType.SGP : undefined;
        }

        return this.isBetBuilderBadgeVisible() ? BetBuilderBadgeType.BetBuilder : undefined;
    }

    private setMarketParameters(): void {
        const parameters = this.getParameters();
        this.marketSubType = parameters?.find((p) => p.key === Tv2MarketParameters.MarketSubType)?.value as MarketSubType;
        this.marketType = parameters?.find((p) => p.key === Tv2MarketParameters.MarketType)?.value as MarketType;

        if (this.optionGroup.groupedMarkets && this.optionGroup.groupedMarkets.length > 0) {
            const isGroupedTemplateIdNotInSecondChanceBadge = !this.marketGroupingConfig.templateIdsForSecondChanceBadge.includes(
                Number(this.optionGroup.templateId),
            );

            this.templateId = isGroupedTemplateIdNotInSecondChanceBadge
                ? this.optionGroup.groupedTemplateIds?.find((id) => this.marketGroupingConfig.templateIdsForSecondChanceBadge.includes(Number(id))) ||
                  this.optionGroup.templateId
                : this.optionGroup.templateId;
        } else {
            this.templateId = this.optionGroup.templateId;
        }

        this.marketLogoShowInHeader =
            this.marketLogoShow &&
            !!this.marketSubTypeLogoService.getMarketSubTypeLogoTitle(
                this.event.id,
                this.event.sport.id,
                this.marketType,
                this.marketSubType,
                this.templateId,
            );
        const periodType = parameters?.find((p) => p.key === Tv2MarketParameters.Period)?.value as PeriodType;
        this.enableCorrectScoreOptionGroupToggle =
            this.eventDetailsConfig.enableCorrectScoreOptionGroupToggle &&
            this.optionGroup.detailedGrouping.displayType === DisplayType.CorrectScore &&
            periodType === PeriodType.RegularTime;
    }

    private setOptionGroupName(): void {
        // For PlayerProps display type with V1 offer source show the period tab as market header
        if (
            this.event.offerSource === OfferSource.V1 &&
            this.optionGroup.detailedGrouping.displayType === DisplayType.PlayerProps &&
            this.optionGroup.periodName &&
            !this.optionGroup.hasSubGroups(EventOptionGroupType.Period)
        ) {
            this.optionGroup.name = this.optionGroup.periodName;
        }
    }

    private getParameters(): Parameter[] | undefined {
        if (this.activePeriodTab) {
            return this.optionGroup.subGroups?.find((sg) => sg.originalId === this.activePeriodTab)?.parameters;
        } else {
            return this.optionGroup.parameters;
        }
    }

    protected readonly NgxFloatUiPlacements = NgxFloatUiPlacements;
}
